import { AccentedHeading, Button, Heading, Paragraph, Text, TwoPanelsPageLayout } from '@expressable/syntax-ds';
import { ChatsCircle, ClipboardText, Clock, Icon, Pencil } from '@phosphor-icons/react';
import { SignupPath } from 'domains/signupDirect/businessLogic/hooks';
import { SIGNUP_LAUNCHDARKLY_EVENTS } from 'domains/signupDirect/misc/launchDarklyTracking';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';

type Items = { title: string; description: string; icon: Icon }[];

const SPLITS_ITEMS: Items = [
  {
    title: 'directSignup.landingPage.panels.split_evaluations.items.1.title',
    description: 'directSignup.landingPage.panels.split_evaluations.items.1.description',
    icon: ChatsCircle,
  },
  {
    title: 'directSignup.landingPage.panels.split_evaluations.items.2.title',
    description: 'directSignup.landingPage.panels.split_evaluations.items.2.description',
    icon: Pencil,
  },
  {
    title: 'directSignup.landingPage.panels.split_evaluations.items.3.title',
    description: 'directSignup.landingPage.panels.split_evaluations.items.3.description',
    icon: Clock,
  },
  {
    title: 'directSignup.landingPage.panels.split_evaluations.items.4.title',
    description: 'directSignup.landingPage.panels.split_evaluations.items.4.description',
    icon: ClipboardText,
  },
];

const SINGLE_ITEMS: Items = [
  {
    title: 'directSignup.landingPage.panels.single_evaluation.items.1.title',
    description: 'directSignup.landingPage.panels.single_evaluation.items.1.description',
    icon: ChatsCircle,
  },
  {
    title: 'directSignup.landingPage.panels.single_evaluation.items.2.title',
    description: 'directSignup.landingPage.panels.single_evaluation.items.2.description',
    icon: Pencil,
  },
  {
    title: 'directSignup.landingPage.panels.single_evaluation.items.3.title',
    description: 'directSignup.landingPage.panels.single_evaluation.items.3.description',
    icon: Clock,
  },
];

export const ExperimentLandingPage = ({ variant }: { variant: SignupPath }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const onContinue = () => {
    if (variant === SignupPath.SPLIT_EVALUATIONS) {
      navigate(routes.signupDirect.evaluation.schedule.split.first);
    } else {
      navigate(routes.signupDirect.evaluation.schedule.single);
    }
  };
  const { track } = useTrack();

  useEffectOnce(() => {
    if (variant === SignupPath.SPLIT_EVALUATIONS) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.EXPERIMENT.LANDING_PAGE_SPLITS);
    } else {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.EXPERIMENT.LANDING_PAGE_SINGLES);
    }
  });

  const client = useLDClient();
  client?.track(SIGNUP_LAUNCHDARKLY_EVENTS.LANDING_PAGE_EXPERIMENT.TRIGGERED);
  return (
    <TwoPanelsPageLayout>
      <TwoPanelsPageLayout.LeftPanel className="py-e48">
        <div>
          <Heading level="h1" className="w-max max-w-full">
            {t(`directSignup.landingPage.panels.${variant}.title`)}
          </Heading>
          <Paragraph>{t(`directSignup.landingPage.panels.${variant}.description`)}</Paragraph>
        </div>
        <Button onClick={onContinue}>{t('directSignup.landingPage.cta')}</Button>
      </TwoPanelsPageLayout.LeftPanel>
      <TwoPanelsPageLayout.RightPanel className="items-center py-e48">
        <AccentedHeading>{t(`directSignup.landingPage.panels.${variant}.rightTitle`)}</AccentedHeading>

        <ul className="flex flex-col gap-e32 tablet-d:gap-e24">
          {(variant === SignupPath.SPLIT_EVALUATIONS ? SPLITS_ITEMS : SINGLE_ITEMS).map((item, index) => (
            <li key={`${variant}-${index}`} className="flex gap-e16 items-start">
              <item.icon className="size-e48 shrink-0" />
              <div>
                <Text variant="body-sm" className="font-bold">
                  {t(item.title)}
                </Text>
                <Text variant="body-sm">{t(item.description)}</Text>
              </div>
            </li>
          ))}
        </ul>
      </TwoPanelsPageLayout.RightPanel>
    </TwoPanelsPageLayout>
  );
};
